import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import * as yup from 'yup';

import Banner, { BannerVariant } from 'components/Banner/Banner';
import FormControls from 'components/FormControls/FormControls';
import InlineForm from 'components/InlineForm/InlineForm';
import Panel from 'components/Panel/Panel';
import TextArea from 'components/TextArea/TextArea';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import useNotification from 'hooks/useNotification';
import useUpdateRegistration, {
  ICasIdRequirementType,
} from 'hooks/useUpdateRegistration';
import { t } from 'locales/i18n';

interface CasIdRequirementProps {
  interviewUid: string;
  data?: ICasIdRequirementType;
}

enum FormFields {
  DESCRIPTION = 'description',
  ENABLE = 'enable',
  LABEL = 'label',
}
interface FormData {
  [FormFields.DESCRIPTION]: string;
  [FormFields.ENABLE]: boolean;
  [FormFields.LABEL]: string;
}

const CasIdRequirement: React.FC<CasIdRequirementProps> = ({
  data,
  interviewUid,
}) => {
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const { updateRegistration, isLoading } = useUpdateRegistration();

  const defaultFormValues = useMemo(
    () => ({
      [FormFields.DESCRIPTION]:
        data?.description ||
        t(
          'Your CAS ID is a 10 digit number located under your name in the upper-right corner of the CAS application.'
        ),
      [FormFields.ENABLE]: Boolean(data),
      [FormFields.LABEL]: data?.label || t('CAS ID'),
    }),
    [data]
  );

  useEffect(() => {
    reset(defaultFormValues);
  }, [data]);

  const FormSchema = useMemo(
    () =>
      yup.object().shape({
        [FormFields.DESCRIPTION]: yup.string(),
        [FormFields.ENABLE]: yup.boolean(),
        [FormFields.LABEL]: yup.string(),
      }),
    [FormFields.DESCRIPTION, FormFields.ENABLE, FormFields.LABEL]
  );

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    watch,
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(FormSchema),
    mode: 'onSubmit',
    defaultValues: defaultFormValues,
  });

  const onSubmit: SubmitHandler<FormData> = async ({
    enable,
    description,
    label,
  }) => {
    const casIdRequirement: ICasIdRequirementType = {
      enable,
      label,
      description,
      interview: interviewUid,
    };
    await updateRegistration(
      { casIdRequirement },
      {
        onSuccess: (data) => {
          showSuccessNotification(
            t('Success'),
            t('Registration updated successfully')
          );
          reset({ ...data });
        },
        onError: (error) => {
          showErrorNotification(
            t('An error occurred while updating registration'),
            error.message
          );
        },
      }
    );
  };

  const casIdFieldsClassName = twMerge(
    'flex',
    'w-ful',
    'overflow-hidden',
    'transition-all',
    watch(FormFields.ENABLE) ? 'h-auto' : 'h-0',
    watch(FormFields.ENABLE) ? '' : '!mt-0'
  );

  return (
    <form
      data-testid="cas-id-requirement-form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <fieldset>
        <Panel title={t('CAS ID Settings')} isFocused={isDirty}>
          <Banner
            message={t('CAS ID details are read-only.')}
            contactLink
            requestChangesTo="CAS ID"
            variant={BannerVariant.INFO}
          />
          <InlineForm
            title={t('Enable CAS ID')}
            description={t(
              'Require applicants to enter their CAS ID during registration.'
            )}
            childrenClassNames="lg:pl-16"
          >
            <Controller
              name={FormFields.ENABLE}
              control={control}
              render={({ field: { onChange, value } }) => (
                <ToggleSwitch
                  disabled
                  enabled={value}
                  setEnabled={onChange}
                  toggleButton="casId"
                />
              )}
            />
          </InlineForm>
          <div className={casIdFieldsClassName}>
            <div className="w-full space-y-6 border-l-4 border-kira-slate-200 pl-4">
              <InlineForm
                title={t('Label')}
                description={t(
                  'Set a custom name for the CAS ID. This will be visible to applicants during check-in.'
                )}
              >
                <TextArea
                  id={`cas-${FormFields.LABEL}`}
                  value={watch(FormFields.LABEL)}
                  isDisabled
                />
              </InlineForm>
              <InlineForm
                title={t('Description')}
                description={t(
                  'Provide any additional information about the CAS ID for applicants.'
                )}
              >
                <TextArea
                  id={`cas-${FormFields.DESCRIPTION}`}
                  value={watch(FormFields.DESCRIPTION)}
                  isDisabled
                />
              </InlineForm>
            </div>
          </div>
          {isDirty && (
            <FormControls
              isLoading={isLoading}
              onCancel={() => reset(defaultFormValues)}
            />
          )}
        </Panel>
      </fieldset>
    </form>
  );
};

export default CasIdRequirement;
